
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useCounties } from "@/hooks/use-counties";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTransactionalEmail } from "@/hooks/use-transactional-email";

const formSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  county: z.string().min(1, "Please select a county"),
  newsletterOptIn: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

interface GuideDownloadFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guide: {
    id: string;
    download_url: string | null;
    title?: string;
  };
}

export function GuideDownloadForm({ open, onOpenChange, guide }: GuideDownloadFormProps) {
  const { data: counties = [] } = useCounties();
  const { sendEmail, isSending } = useTransactionalEmail();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      county: "",
      newsletterOptIn: false,
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      // Record the download attempt
      await supabase.from('guide_downloads').insert({
        guide_id: guide.id,
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        county: data.county,
        newsletter_opt_in: data.newsletterOptIn,
      });

      // Send follow-up email with guide link and CTA
      if (guide.download_url) {
        try {
          await sendEmail({
            type: 'guide_download',
            data: {
              firstName: data.firstName,
              email: data.email,
              guideTitle: guide.title || "Nursing Home Guide",
              guideUrl: guide.download_url,
              waitlistUrl: "https://www.grandpal.co/waitlist",
            },
          });
          
          console.log("Follow-up email sent successfully");
        } catch (emailError) {
          console.error("Failed to send follow-up email:", emailError);
          // Continue with download even if email fails
        }
      }

      // Start the download
      if (guide.download_url) {
        window.open(guide.download_url, '_blank');
      }

      toast.success("Download started", {
        description: "Your guide will download shortly and has been sent to your email."
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Download error:', error);
      toast.error("Failed to process download", {
        description: "Please try again later."
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Download Guide</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your first name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your last name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your email" type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="county"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>County</FormLabel>
                  <FormControl>
                    <select
                      className="w-full p-2 border rounded-md"
                      {...field}
                    >
                      <option value="">Select a county</option>
                      {counties.map((county) => (
                        <option key={county.id} value={county.name}>
                          {county.name}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="newsletterOptIn"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="font-normal">
                      Keep me updated about new guides and resources
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={isSending}>
              {isSending ? "Sending..." : "Download Guide"}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
